import { use } from "react";
import { buildHref } from "@softmax/web-addresses";
import { QueryViewerContext } from "@softmax/web-features/data";
import {
  coworldDirectoryQueryOptions,
  policySearchQueryOptions,
  matchCoworlds,
  policySearchRows,
} from "@/features/search/records";

import { leagueDirectoryQueryOptions } from "@softmax/web-features/site/queries";
import { switchLeagueHref } from "@softmax/web-features/site/records";
import {
  type SearchInputs,
  maxResultsPerGroup,
  matchesSearch,
  pillSearchGroup,
} from "./pill-search-records";
import { useSearchQuery } from "./use-search-query";
import { viewerProgressQueryOptions } from "./pill-search.queries";
export function useSiteSearch({
  query,
  enabled,
  ready,
  member,
}: SearchInputs & {
  member: boolean;
}) {
  const viewerId = use(QueryViewerContext);
  const directory = useSearchQuery({
    ...coworldDirectoryQueryOptions(),
    enabled,
  });
  const leagues = useSearchQuery({
    ...leagueDirectoryQueryOptions({ viewerId: member ? viewerId : null }),
    enabled,
  });
  const progress = useSearchQuery({
    ...viewerProgressQueryOptions(),
    enabled: enabled && member,
  });
  const players = useSearchQuery({
    ...policySearchQueryOptions({ query, limit: maxResultsPerGroup }),
    enabled: enabled && ready && member,
  });
  const activeGames =
    member && progress.data ? new Map(progress.data) : undefined;
  const coworldRows = matchCoworlds({
    directory: directory.data ?? [],
    query,
    limit: maxResultsPerGroup + 4,
    inProgress: activeGames,
  });
  const leagueRows = (leagues.data?.entries ?? [])
    .filter(
      (entry) =>
        entry.route.game.catalog_status === "listed" &&
        matchesSearch({ query: query, fields: [entry.name, entry.coworld] }),
    )
    .map((entry) => ({
      key: entry.league_id,
      href: switchLeagueHref({
        pathname: buildHref({ name: "browse" }),
        target: entry,
      }),
      label: entry.name,
      secondary: entry.coworld ?? undefined,
    }));
  const groups = [
    pillSearchGroup({
      heading: "Coworlds",
      rows: coworldRows,
      queries: [directory],
    }),
    pillSearchGroup({
      heading: "Leagues",
      rows: leagueRows,
      queries: [leagues],
    }),
  ];

  if (ready && member)
    groups.push(
      pillSearchGroup({
        heading: "Players",
        rows: policySearchRows(players.data ?? []).map((row) => ({
          ...row,
          label: row.secondary ?? row.label,
          secondary: row.secondary ? row.label : undefined,
        })),
        queries: [players],
      }),
    );

  return groups;
}
